import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ChevronRight, Search, BookOpen, Rocket, Plug, ShieldCheck,
  Cpu, Boxes, Terminal, GitBranch, Layers, Webhook, Code2, Bot,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useLeadDialog } from '../context/LeadDialogContext';

const CATS = [
  {
    icon: Rocket,
    title: 'Getting started',
    desc: 'Stand up MetaxuOne in under an hour. Concepts, install, and first signals.',
    items: ['Platform overview', 'Quickstart in 30 minutes', 'Architecture concepts', 'Provisioning your tenant'],
  },
  {
    icon: Layers,
    title: 'Platform',
    desc: 'Observability, the context model, and the agent runtime.',
    items: ['Context graph', 'Observability planes', 'Agent runtime', 'Policy engine'],
  },
  {
    icon: Bot,
    title: 'AI Agents',
    desc: 'Build, deploy, and govern action-grounded agents.',
    items: ['Agent SDK', 'Tools & actions', 'Memory & state', 'Evaluation harness'],
  },
  {
    icon: Plug,
    title: 'Integrations',
    desc: '120+ connectors for clouds, infra, ITSM, and data sources.',
    items: ['Cloud (AWS, Azure, GCP)', 'Kubernetes & service mesh', 'ITSM (ServiceNow, Jira)', 'Data lakes & warehouses'],
  },
  {
    icon: Code2,
    title: 'API reference',
    desc: 'REST and gRPC reference for every surface of the platform.',
    items: ['Auth & tenancy', 'Telemetry API', 'Agents API', 'Webhooks'],
  },
  {
    icon: Terminal,
    title: 'CLI & SDKs',
    desc: 'Command-line tools and language SDKs.',
    items: ['mox CLI', 'Python SDK', 'Go SDK', 'TypeScript SDK'],
  },
  {
    icon: ShieldCheck,
    title: 'Security & governance',
    desc: 'Compliance, RBAC, audit and data residency.',
    items: ['RBAC & SSO', 'Audit trails', 'Data residency', 'Compliance posture'],
  },
  {
    icon: GitBranch,
    title: 'Operations',
    desc: 'Upgrades, capacity, backup, and disaster recovery.',
    items: ['Upgrade guide', 'Capacity planning', 'Backup & restore', 'DR runbook'],
  },
];

const QUICKLINKS = [
  { label: 'Quickstart', icon: Rocket },
  { label: 'API reference', icon: Code2 },
  { label: 'Agent SDK', icon: Bot },
  { label: 'Webhooks', icon: Webhook },
  { label: 'Connectors', icon: Plug },
  { label: 'CLI', icon: Terminal },
];

const Docs = () => {
  const { open: openLead } = useLeadDialog();
  const [q, setQ] = useState('');
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const filtered = q.trim().length > 0
    ? CATS.map((c) => ({ ...c, items: c.items.filter((i) => i.toLowerCase().includes(q.toLowerCase())) }))
        .filter((c) => c.title.toLowerCase().includes(q.toLowerCase()) || c.items.length > 0)
    : CATS;

  return (
    <div className="bg-black min-h-screen" data-testid="docs-page">
      <Navbar />
      <main>
        {/* HERO */}
        <section className="relative overflow-hidden bg-black border-b border-neutral-900">
          <div aria-hidden className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(60% 50% at 50% 0%, rgba(59,130,246,0.22), transparent), radial-gradient(50% 60% at 10% 30%, rgba(255,255,255,0.05), transparent)',
          }} />
          <div aria-hidden className="absolute inset-0 opacity-[0.35] pointer-events-none" style={{
            backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.05) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
            maskImage: 'radial-gradient(ellipse at top, black 30%, transparent 75%)',
          }} />
          <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-16 pb-16">
            <nav className="flex items-center gap-1.5 text-[12.5px] text-neutral-500">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-neutral-400">Resources</span>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-neutral-300">Documentation</span>
            </nav>
            <div className="mt-7 max-w-3xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/30">
                <BookOpen className="w-3.5 h-3.5 text-blue-400" />
                <span className="text-[11.5px] uppercase tracking-[0.15em] text-blue-300">Documentation</span>
              </div>
              <h1 className="mt-4 text-[34px] sm:text-[48px] md:text-[60px] lg:text-[72px] leading-[1.02] sm:leading-[0.98] tracking-[-0.03em] font-semibold text-white">
                Build with <span className="italic font-light brand-gradient-text">MetaxuOne.</span>
              </h1>
              <p className="mt-5 text-[16.5px] lg:text-[18px] text-neutral-300 leading-relaxed">
                Everything you need to deploy the platform, integrate your stack, build agents, and operate at scale — guides, references, runbooks and SDKs.
              </p>
            </div>

            {/* Search */}
            <div className="mt-10 relative max-w-2xl">
              <Search className="w-4 h-4 text-neutral-500 absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search docs · e.g. agents, webhooks, RBAC"
                data-testid="docs-search-input"
                className="w-full h-12 pl-11 pr-4 rounded-full bg-neutral-950 border border-neutral-800 focus:border-blue-500/60 focus:outline-none text-white text-[14px] placeholder:text-neutral-500"
              />
            </div>

            {/* Quick links */}
            <div className="mt-6 flex items-center gap-2 flex-wrap">
              {QUICKLINKS.map((q) => {
                const I = q.icon;
                return (
                  <span
                    key={q.label}
                    className="inline-flex items-center gap-1.5 text-[12.5px] px-3 py-1.5 rounded-full border border-neutral-800 text-neutral-300 bg-neutral-950"
                  >
                    <I className="w-3.5 h-3.5 text-blue-300" /> {q.label}
                  </span>
                );
              })}
            </div>
          </div>
        </section>

        {/* GETTING STARTED CALLOUT */}
        <section className="bg-neutral-950 border-b border-neutral-900">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 lg:py-16">
            <div className="grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7">
                <p className="text-[12.5px] uppercase tracking-[0.18em] text-blue-400">Start here</p>
                <h2 className="mt-3 text-[26px] sm:text-[34px] lg:text-[40px] tracking-[-0.02em] font-semibold text-white">
                  From zero to your first <span className="italic font-light brand-gradient-text">self-healing workflow</span> in 30 minutes.
                </h2>
                <p className="mt-4 text-[15.5px] text-neutral-300 max-w-xl">
                  Provision a tenant, connect a data source, deploy an agent and watch it triage and remediate a synthetic incident — end to end.
                </p>
              </div>
              <div className="lg:col-span-5">
                <ol className="space-y-2">
                  {['Provision tenant', 'Connect a data source', 'Deploy your first agent', 'Run a remediation'].map((s, i) => (
                    <li key={s} className="flex items-center gap-3 rounded-xl border border-neutral-800 bg-black p-4">
                      <span className="w-8 h-8 rounded-lg bg-blue-500/15 border border-blue-500/40 text-blue-300 text-[12.5px] font-semibold flex items-center justify-center">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <span className="text-[14px] text-neutral-200">{s}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-neutral-600 ml-auto" />
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </section>

        {/* CATEGORIES GRID */}
        <section className="bg-black border-b border-neutral-900">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
            <p className="text-[12.5px] uppercase tracking-[0.18em] text-blue-400">Browse documentation</p>
            <h2 className="mt-3 text-[26px] sm:text-[32px] lg:text-[40px] tracking-[-0.02em] font-semibold text-white">All topics</h2>

            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((c) => {
                const I = c.icon;
                return (
                  <div key={c.title} className="rounded-2xl border border-neutral-800 bg-neutral-950 p-6 hover:border-neutral-600 transition-colors">
                    <div className="flex items-center gap-3">
                      <span className="w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-300 inline-flex items-center justify-center">
                        <I className="w-5 h-5" />
                      </span>
                      <h3 className="text-[17px] font-semibold text-white">{c.title}</h3>
                    </div>
                    <p className="mt-3 text-[13.5px] text-neutral-400 leading-snug">{c.desc}</p>
                    <ul className="mt-5 space-y-2">
                      {c.items.map((i) => (
                        <li key={i} className="flex items-center justify-between text-[13.5px] text-neutral-200 hover:text-white">
                          <span>{i}</span>
                          <ArrowRight className="w-3.5 h-3.5 text-neutral-600" />
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <div className="col-span-full text-center text-[14px] text-neutral-400 py-10">
                  No topics matched <span className="text-white font-medium">&ldquo;{q}&rdquo;</span>. Try a broader keyword.
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ARCH STRIP */}
        <section className="bg-neutral-950 border-b border-neutral-900">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
            <p className="text-[12.5px] uppercase tracking-[0.18em] text-blue-400">Reference architecture</p>
            <div className="mt-6 grid lg:grid-cols-4 gap-3">
              {[
                { I: Cpu, k: 'Telemetry plane', v: 'High-throughput ingestion, normalization and routing.' },
                { I: Boxes, k: 'Context graph', v: 'Unified topology, ownership and dependency model.' },
                { I: Bot, k: 'Agent runtime', v: 'Policy-gated execution with audit and rollback.' },
                { I: ShieldCheck, k: 'Governance', v: 'RBAC, tenancy, residency and compliance controls.' },
              ].map((b) => {
                const Icon = b.I;
                return (
                  <div key={b.k} className="rounded-2xl border border-neutral-800 bg-black p-5">
                    <Icon className="w-4 h-4 text-blue-300" />
                    <div className="mt-3 text-[14px] font-semibold text-white">{b.k}</div>
                    <div className="mt-1 text-[12.5px] text-neutral-400 leading-snug">{b.v}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-black">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-24">
            <div className="grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <h3 className="text-[28px] sm:text-[34px] lg:text-[40px] leading-[1.05] tracking-[-0.02em] font-semibold text-white">
                  Need a hand <span className="italic font-light brand-gradient-text">getting started?</span>
                </h3>
                <p className="mt-4 text-[15.5px] text-neutral-300 max-w-xl">
                  Talk to a solutions architect to map your environment to MetaxuOne — we&apos;ll walk you through architecture, integrations and the rollout plan.
                </p>
              </div>
              <div className="lg:col-span-5 flex lg:justify-end">
                <Button
                  onClick={() => openLead({ source: 'docs-cta', title: 'Talk to a solutions architect' })}
                  className="h-12 px-6 rounded-full bg-white hover:bg-neutral-200 text-neutral-950 text-[14px] font-medium inline-flex items-center gap-2"
                  data-testid="docs-cta-btn"
                >
                  Talk to an architect <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Docs;
